<template>
  <div class="user-dashboard">
    <!-- 頁面頭部 -->
    <div class="dashboard-header">
      <div class="container">
        <div class="header-content">
          <div class="user-info">
            <div class="user-details">
              <h1>會員專區</h1>
            </div>
          </div>
          <div class="quick-stats">
            <div class="stat-item clickable" @click="goToOrders">
              <span class="stat-number">{{ totalOrders }}</span>
              <span class="stat-label">總訂單</span>
            </div>
            <div class="stat-item clickable" @click="goToCoupons">
              <span class="stat-number">{{ availableCoupons }}</span>
              <span class="stat-label">可用優惠券</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要內容區 -->
    <div class="dashboard-main">
      <div class="container">
        <div class="dashboard-layout">
          <!-- 側邊導航 -->
          <div class="sidebar">
            <nav class="nav-menu">
              <a
                href="#"
                :class="['nav-item', { active: activeTab === 'profile' }]"
                @click.prevent="setActiveTab('profile')"
              >
                <i class="fas fa-user"></i>
                個人資料
              </a>
              <a
                href="#"
                :class="['nav-item', { active: activeTab === 'orders' }]"
                @click.prevent="setActiveTab('orders')"
              >
                <i class="fas fa-shopping-bag"></i>
                我的訂單
              </a>
              <a
                href="#"
                :class="['nav-item', { active: activeTab === 'coupons' }]"
                @click.prevent="setActiveTab('coupons')"
              >
                <i class="fas fa-ticket-alt"></i>
                優惠券
              </a>
              <a
                href="#"
                :class="['nav-item', { active: activeTab === 'settings' }]"
                @click.prevent="setActiveTab('settings')"
              >
                <i class="fas fa-cog"></i>
                帳戶設定
              </a>
            </nav>
          </div>

          <!-- 內容區域 -->
          <div class="content-area">
            <!-- 個人資料頁面 -->
            <div v-if="activeTab === 'profile'" class="profile-section">
              <div class="section-header">
                <h2>個人資料</h2>
                <!-- 權限區分顯示 -->
                <div class="auth-badge-container">
                  <div
                    class="auth-badge"
                    :style="{ backgroundColor: getAuthColor(userProfile.auth) }"
                  >
                    <i class="fas fa-shield-alt"></i>
                    {{ getAuthLabel(userProfile.auth) }}
                  </div>
                  <div
                    v-if="userProfile.auth === 'Doctor'"
                    class="doctor-verified"
                  >
                    <i class="fas fa-check-circle"></i>
                    醫師認證已通過
                  </div>
                </div>
                <button class="btn-primary-edit" @click="toggleEditMode">
                  {{ isEditing ? "取消編輯" : "編輯資料" }}
                </button>
              </div>

              <div class="profile-form">
                <!-- ✅ 一般會員表單 -->
                <div v-if="userProfile.auth === 'User'" class="form-section">
                  <h3>基本資料</h3>
                  <div class="form-row">
                    <div class="form-group">
                      <label>姓名 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.fullName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入姓名"
                      />
                    </div>
                    <div class="form-group">
                      <label>電話 (必填)</label>
                      <input
                        type="tel"
                        v-model="editProfile.phoneNumber"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入電話號碼"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>地址 (必填)</label>
                      <textarea
                        v-model="editProfile.address"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        rows="3"
                        placeholder="請輸入地址"
                      ></textarea>
                    </div>
                    <div class="form-group">
                      <label>電子信箱</label>
                      <input
                        type="email"
                        v-model="editProfile.email"
                        disabled
                        :class="{ editing: isEditing }"
                        placeholder="請輸入電子信箱"
                      />
                    </div>
                  </div>

                  <!-- ✅ 醫師執照上傳區塊 -->
                  <div class="form-row">
                    <div class="form-group full-width">
                      <label>醫師執照上傳</label>
                      <p class="field-description">
                        上傳獸醫師執照以申請醫師權限
                      </p>

                      <!-- 已上傳執照顯示 -->
                      <div v-if="userProfile.picture" class="license-uploaded">
                        <div class="upload-success">
                          <i class="fas fa-check-circle"></i>
                          <div class="upload-info">
                            <h4>執照已上傳</h4>
                            <p>
                              已於
                              {{
                                formatDate(
                                  userProfile.doctorCheckDate ||
                                    userProfile.updatedDate,
                                )
                              }}
                              提交審核
                            </p>
                          </div>
                          <button class="btn-outline" @click="previewLicense">
                            查看
                          </button>
                        </div>
                      </div>

                      <!-- 上傳區域 -->
                      <div v-else class="license-upload">
                        <div
                          class="upload-area"
                          :class="{ 'drag-over': isDragOver }"
                          @drop="handleDrop"
                          @dragover.prevent="isDragOver = true"
                          @dragleave.prevent="isDragOver = false"
                          @click="triggerFileInput"
                        >
                          <div v-if="!previewImage" class="upload-placeholder">
                            <i class="fas fa-cloud-upload-alt"></i>
                            <h4>上傳獸醫師執照</h4>
                            <p>點擊選擇檔案或拖拽檔案至此區域</p>
                            <p class="upload-note">
                              支援 JPG、PNG 格式，檔案大小不超過 5MB
                            </p>
                          </div>

                          <!-- 預覽區域 -->
                          <div v-if="previewImage" class="upload-preview">
                            <img :src="previewImage" alt="執照預覽" />
                            <div class="upload-actions">
                              <button
                                type="button"
                                class="btn-outline"
                                @click.stop="removeImage"
                              >
                                <i class="fas fa-trash"></i> 移除
                              </button>
                              <button
                                type="button"
                                class="btn-outline"
                                @click.stop="triggerFileInput"
                              >
                                <i class="fas fa-edit"></i> 更換
                              </button>
                            </div>
                          </div>
                        </div>

                        <input
                          ref="fileInput"
                          type="file"
                          accept="image/*"
                          style="display: none"
                          @change="handleFileSelect"
                        />

                        <!-- 上傳按鈕 -->
                        <div v-if="selectedFile" class="upload-submit">
                          <button class="btn-primary" @click="uploadLicense">
                            <i class="fas fa-upload"></i> 提交執照審核
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ✅ 廠商(經銷商) 表單 -->
                <div
                  v-if="userProfile.auth === 'Supplier'"
                  class="form-section"
                >
                  <h3>廠商資料</h3>
                  <div class="auth-badge-container-supplier">
                    <div
                      class="auth-badge"
                      :style="{
                        backgroundColor: getAuthColor(userProfile.auth),
                      }"
                    >
                      <i class="fas fa-shield-alt"></i>
                      {{ getAuthLabel(userProfile.auth) }}
                    </div>
                    <div
                      v-if="userProfile.auth === 'Doctor'"
                      class="doctor-verified"
                    >
                      <i class="fas fa-check-circle"></i>
                      醫師認證已通過
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>公司名稱 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.companyName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入公司名稱"
                      />
                    </div>
                    <div class="form-group">
                      <label>聯絡人姓名 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.fullName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入聯絡人姓名"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>職稱</label>
                      <input
                        type="text"
                        v-model="editProfile.jobTitle"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入職稱"
                      />
                    </div>
                    <div class="form-group">
                      <label>統一編號 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.taxId"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入統一編號"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>公司地址 (必填)</label>
                      <textarea
                        v-model="editProfile.address"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        rows="3"
                        placeholder="請輸入公司地址"
                      ></textarea>
                    </div>
                    <div class="form-group">
                      <label>聯絡電話 (必填)</label>
                      <input
                        type="tel"
                        v-model="editProfile.phoneNumber"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入聯絡電話"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>電子信箱</label>
                      <input
                        type="email"
                        v-model="editProfile.email"
                        disabled
                        :class="{ editing: isEditing }"
                        placeholder="請輸入電子信箱"
                      />
                    </div>
                  </div>

                  <!-- ✅ 營業執照上傳 -->
                  <div class="form-row">
                    <div class="form-group full-width">
                      <label>營業執照</label>
                      <p class="field-description">
                        請上傳營業執照以完善公司資料
                      </p>

                      <!-- 已上傳執照顯示 -->
                      <div v-if="userProfile.picture" class="license-uploaded">
                        <div class="upload-success">
                          <i class="fas fa-check-circle"></i>
                          <div class="upload-info">
                            <h4>營業執照已上傳</h4>
                            <p>
                              已於
                              {{ formatDate(userProfile.updatedDate) }} 上傳
                            </p>
                          </div>
                          <button class="btn-outline" @click="previewLicense">
                            查看
                          </button>
                        </div>
                      </div>

                      <!-- 上傳區域 -->
                      <div v-else class="license-upload">
                        <div
                          class="upload-area"
                          :class="{ 'drag-over': isDragOver }"
                          @drop="handleDrop"
                          @dragover.prevent="isDragOver = true"
                          @dragleave.prevent="isDragOver = false"
                          @click="triggerFileInput"
                        >
                          <div v-if="!previewImage" class="upload-placeholder">
                            <i class="fas fa-cloud-upload-alt"></i>
                            <h4>上傳營業執照</h4>
                            <p>點擊選擇檔案或拖拽檔案至此區域</p>
                            <p class="upload-note">
                              支援 JPG、PNG 格式，檔案大小不超過 5MB
                            </p>
                          </div>

                          <!-- 預覽區域 -->
                          <div v-if="previewImage" class="upload-preview">
                            <img :src="previewImage" alt="營業執照預覽" />
                            <div class="upload-actions">
                              <button
                                type="button"
                                class="btn-outline"
                                @click.stop="removeImage"
                              >
                                <i class="fas fa-trash"></i> 移除
                              </button>
                              <button
                                type="button"
                                class="btn-outline"
                                @click.stop="triggerFileInput"
                              >
                                <i class="fas fa-edit"></i> 更換
                              </button>
                            </div>
                          </div>
                        </div>

                        <input
                          ref="fileInput"
                          type="file"
                          accept="image/*"
                          style="display: none"
                          @change="handleFileSelect"
                        />

                        <!-- 上傳按鈕 -->
                        <div v-if="selectedFile" class="upload-submit">
                          <button class="btn-primary" @click="uploadLicense">
                            <i class="fas fa-upload"></i> 上傳營業執照
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ✅ 廠商(藥局) 表單 -->
                <div
                  v-if="userProfile.auth === 'DrugStore'"
                  class="form-section"
                >
                  <h3>藥局資料</h3>
                  <div class="auth-badge-container-supplier">
                    <div
                      class="auth-badge"
                      :style="{
                        backgroundColor: getAuthColor(userProfile.auth),
                      }"
                    >
                      <i class="fas fa-shield-alt"></i>
                      {{ getAuthLabel(userProfile.auth) }}
                    </div>
                    <div
                      v-if="userProfile.auth === 'Doctor'"
                      class="doctor-verified"
                    >
                      <i class="fas fa-check-circle"></i>
                      醫師認證已通過
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>公司名稱 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.companyName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入藥局名稱"
                      />
                    </div>
                    <div class="form-group">
                      <label>聯絡人姓名 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.fullName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入聯絡人姓名"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>職稱</label>
                      <input
                        type="text"
                        v-model="editProfile.jobTitle"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入職稱"
                      />
                    </div>
                    <div class="form-group">
                      <label>統一編號 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.taxId"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入統一編號"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>公司地址 (必填)</label>
                      <textarea
                        v-model="editProfile.address"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        rows="3"
                        placeholder="請輸入藥局地址"
                      ></textarea>
                    </div>
                    <div class="form-group">
                      <label>聯絡電話 (必填)</label>
                      <input
                        type="tel"
                        v-model="editProfile.phoneNumber"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入聯絡電話"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>電子信箱</label>
                      <input
                        type="email"
                        v-model="editProfile.email"
                        disabled
                        :class="{ editing: isEditing }"
                        placeholder="請輸入電子信箱"
                      />
                    </div>
                  </div>

                  <!-- ✅ 藥商許可執照上傳 -->
                  <div class="form-row">
                    <div class="form-group full-width">
                      <label>藥商許可執照</label>
                      <p class="field-description">
                        請上傳藥商許可執照以完善公司資料
                      </p>

                      <!-- 已上傳執照顯示 -->
                      <div v-if="userProfile.picture" class="license-uploaded">
                        <div class="upload-success">
                          <i class="fas fa-check-circle"></i>
                          <div class="upload-info">
                            <h4>藥商許可執照已上傳</h4>
                            <p>
                              已於
                              {{ formatDate(userProfile.updatedDate) }} 上傳
                            </p>
                          </div>
                          <button class="btn-outline" @click="previewLicense">
                            查看
                          </button>
                        </div>
                      </div>

                      <!-- 上傳區域 -->
                      <div v-else class="license-upload">
                        <div
                          class="upload-area"
                          :class="{ 'drag-over': isDragOver }"
                          @drop="handleDrop"
                          @dragover.prevent="isDragOver = true"
                          @dragleave.prevent="isDragOver = false"
                          @click="triggerFileInput"
                        >
                          <div v-if="!previewImage" class="upload-placeholder">
                            <i class="fas fa-cloud-upload-alt"></i>
                            <h4>上傳藥商許可執照</h4>
                            <p>點擊選擇檔案或拖拽檔案至此區域</p>
                            <p class="upload-note">
                              支援 JPG、PNG 格式，檔案大小不超過 5MB
                            </p>
                          </div>

                          <!-- 預覽區域 -->
                          <div v-if="previewImage" class="upload-preview">
                            <img :src="previewImage" alt="藥商許可執照預覽" />
                            <div class="upload-actions">
                              <button
                                type="button"
                                class="btn-outline"
                                @click.stop="removeImage"
                              >
                                <i class="fas fa-trash"></i> 移除
                              </button>
                              <button
                                type="button"
                                class="btn-outline"
                                @click.stop="triggerFileInput"
                              >
                                <i class="fas fa-edit"></i> 更換
                              </button>
                            </div>
                          </div>
                        </div>

                        <input
                          ref="fileInput"
                          type="file"
                          accept="image/*"
                          style="display: none"
                          @change="handleFileSelect"
                        />

                        <!-- 上傳按鈕 -->
                        <div v-if="selectedFile" class="upload-submit">
                          <button class="btn-primary" @click="uploadLicense">
                            <i class="fas fa-upload"></i> 上傳藥商執照
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ✅ 動物醫師 表單 -->
                <div v-if="userProfile.auth === 'Doctor'" class="form-section">
                  <h3>醫師資料</h3>
                  <div class="auth-badge-container-supplier">
                    <div
                      class="auth-badge"
                      :style="{
                        backgroundColor: getAuthColor(userProfile.auth),
                      }"
                    >
                      <i class="fas fa-shield-alt"></i>
                      {{ getAuthLabel(userProfile.auth) }}
                    </div>
                    <div
                      v-if="userProfile.auth === 'Doctor'"
                      class="doctor-verified"
                    >
                      <i class="fas fa-check-circle"></i>
                      醫師認證已通過
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>醫院名稱 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.companyName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入醫院名稱"
                      />
                    </div>
                    <div class="form-group">
                      <label>醫生姓名 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.fullName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入醫生姓名"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>職稱</label>
                      <input
                        type="text"
                        v-model="editProfile.jobTitle"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入職稱"
                      />
                    </div>
                    <div class="form-group">
                      <label>聯絡電話 (必填)</label>
                      <input
                        type="tel"
                        v-model="editProfile.phoneNumber"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入聯絡電話"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>醫院地址 (必填)</label>
                      <textarea
                        v-model="editProfile.address"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        rows="3"
                        placeholder="請輸入醫院地址"
                      ></textarea>
                    </div>
                    <div class="form-group">
                      <label>電子信箱</label>
                      <input
                        type="email"
                        v-model="editProfile.email"
                        disabled
                        :class="{ editing: isEditing }"
                        placeholder="請輸入電子信箱"
                      />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-group full-width">
                      <label>獸醫師執照</label>

                      <!-- 有執照時的顯示 -->
                      <div v-if="userProfile.picture" class="license-display">
                        <div class="license-preview">
                          <img
                            :src="userProfile.picture"
                            alt="獸醫師執照"
                            @click="previewLicense"
                          />
                          <div class="license-info">
                            <p class="verified">
                              <i class="fas fa-check-circle"></i> 已通過審核
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- 沒有執照時的顯示 -->
                      <div v-else class="no-license">
                        <div class="no-license-placeholder">
                          <p>尚未上傳獸醫師執照</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ✅ 僅客戶建檔 表單 (Note) -->
                <div v-if="userProfile.auth === 'Note'" class="form-section">
                  <h3>基本資料</h3>
                  <div class="form-row">
                    <div class="form-group">
                      <label>姓名 (必填)</label>
                      <input
                        type="text"
                        v-model="editProfile.fullName"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入姓名"
                      />
                    </div>
                    <div class="form-group">
                      <label>電話 (必填)</label>
                      <input
                        type="tel"
                        v-model="editProfile.phoneNumber"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        placeholder="請輸入電話號碼"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label>地址 (必填)</label>
                      <textarea
                        v-model="editProfile.address"
                        :disabled="!isEditing"
                        :class="{ editing: isEditing }"
                        rows="3"
                        placeholder="請輸入地址"
                      ></textarea>
                    </div>
                    <div class="form-group">
                      <label>電子信箱</label>
                      <input
                        type="email"
                        v-model="editProfile.email"
                        disabled
                        :class="{ editing: isEditing }"
                        placeholder="請輸入電子信箱"
                      />
                    </div>
                  </div>
                </div>

                <div v-if="isEditing" class="form-actions">
                  <button class="btn-secondary" @click="cancelEdit">
                    取消
                  </button>
                  <button class="btn-primary" @click="saveProfile">儲存</button>
                </div>
              </div>
            </div>

            <!-- 訂單頁面 -->
            <div v-if="activeTab === 'orders'" class="orders-section">
              <div class="section-header">
                <h2>我的訂單</h2>
                <div class="order-filters">
                  <!-- ✅ 電腦版:按鈕組 -->
                  <!-- ✅ 全裝置通用：下拉選單版本 -->
                  <div class="order-filters">
                    <select
                      v-model="selectedOrderStatus"
                      @change="filterOrders(selectedOrderStatus)"
                      class="order-status-select"
                    >
                      <option
                        v-for="status in orderStatuses"
                        :key="status.key"
                        :value="status.key"
                      >
                        {{ status.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="orders-list">
                <div
                  v-for="order in paginatedOrders"
                  :key="order.id"
                  class="order-card"
                >
                  <div class="order-header">
                    <div class="order-info">
                      <span class="order-number"
                        >訂單編號：{{ order.orderNumber }}</span
                      >
                      <span class="order-date">{{
                        formatDate(order.createdAt)
                      }}</span>
                      <span class="payment-method"
                        >付款方式：{{ order.paymentMethod }}</span
                      >
                    </div>
                    <div class="order-status-group">
                      <!-- ✅ 新增付款狀態 -->
                      <span
                        :class="[
                          'payment-status',
                          order.paymentStatus ? 'paid' : 'unpaid',
                        ]"
                      >
                        {{ order.paymentStatus ? "已付款" : "未付款" }}
                      </span>
                      <span :class="['order-status', order.status]">
                        {{ getOrderStatusLabel(order.status) }}
                      </span>
                    </div>
                  </div>

                  <div class="order-items">
                    <div
                      v-for="item in getDisplayItems(order)"
                      :key="item.id"
                      class="order-item"
                    >
                      <div class="item-details">
                        <h4>{{ item.name }}</h4>
                        <p>數量：{{ item.quantity }}</p>
                      </div>
                      <div class="item-price">{{ item.price }}</div>
                    </div>
                  </div>

                  <div
                    v-if="shouldShowMore(order)"
                    class="show-more"
                    @click="toggleOrderExpand(order)"
                  >
                    <span v-if="!expandedOrders[order.id]">顯示更多</span>
                    <span v-else>收起</span>
                  </div>

                  <div class="order-footer">
                    <div class="order-total">
                      總金額：{{ order.totalAmount }}
                    </div>
                    <div class="order-actions">
                      <!-- ✅ 取消訂單按鈕 -->
                      <button
                        v-if="order.status === '待處理'"
                        class="btn-outline btn-cancel"
                        @click="cancelOrder(order)"
                      >
                        <i class="fas fa-times-circle"></i> 取消訂單
                      </button>

                      <!-- ✅ 申請退/換貨按鈕 -->
                      <button
                        v-if="order.status === '已完成'"
                        class="btn-primary-more"
                        @click="openReturnModal(order)"
                      >
                        <i class="fas fa-undo"></i> 申請退/換貨
                      </button>

                      <!-- 申請退貨後顯示退貨進度 -->
                      <button
                        v-if="order.status === '申請退貨'"
                        class="btn-outline btn-return-status"
                        @click="openReturnStatusModal(order)"
                      >
                        <i class="fas fa-info-circle"></i> 退貨狀態
                      </button>
                    </div>
                  </div>
                </div>

                <div v-if="filteredOrders.length === 0" class="empty-state">
                  <i class="fas fa-shopping-bag"></i>
                  <p>暫無訂單記錄</p>
                </div>
              </div>

              <!-- 分頁 -->
              <div class="pagination" v-if="totalOrderPages > 1">
                <button
                  class="page-btn"
                  :disabled="currentOrderPage === 1"
                  @click="currentOrderPage--"
                >
                  上一頁
                </button>

                <span class="page-number">
                  {{ currentOrderPage }} / {{ totalOrderPages }}
                </span>

                <button
                  class="page-btn"
                  :disabled="currentOrderPage === totalOrderPages"
                  @click="currentOrderPage++"
                >
                  下一頁
                </button>
              </div>
            </div>

            <!-- 優惠券頁面 -->
            <div v-if="activeTab === 'coupons'" class="coupons-section">
              <div class="section-header">
                <h2>優惠券</h2>
                <div class="coupon-filters">
                  <button
                    v-for="status in couponStatuses"
                    :key="status.key"
                    :class="[
                      'filter-btn',
                      { active: selectedCouponStatus === status.key },
                    ]"
                    @click="filterCoupons(status.key)"
                  >
                    {{ status.label }}
                  </button>
                </div>
              </div>

              <div class="coupons-grid">
                <div
                  v-for="coupon in filteredCoupons"
                  :key="coupon.id"
                  :class="['coupon-card', coupon.status]"
                >
                  <div class="coupon-header">
                    <div class="coupon-type">{{ coupon.type }}</div>
                    <div :class="['coupon-status-badge', coupon.status]">
                      {{ getCouponStatusLabel(coupon.status) }}
                    </div>
                  </div>

                  <div class="coupon-content">
                    <div class="coupon-title">{{ coupon.title }}</div>
                    <div class="coupon-description">
                      {{ coupon.description }}
                    </div>
                    <div class="coupon-conditions">
                      <span v-if="coupon.minAmount"
                        >滿 {{ coupon.minAmount }} 可用</span
                      >
                      <span class="coupon-expiry"
                        >有效期至：{{ formatDate(coupon.expiryDate) }}</span
                      >
                    </div>
                  </div>

                  <div class="coupon-footer">
                    <div class="coupon-value">
                      <!-- ✅ 商品兌換券 -->
                      <template v-if="coupon.type === '商品券'">
                        🎁 可兌換商品：{{ coupon.productName || "指定商品" }}
                      </template>

                      <!-- ✅ 折抵金額券 -->
                      <template v-else-if="coupon.discountType === 'amount'">
                        減 {{ coupon.discountValue }} 元
                      </template>

                      <!-- ✅ 折數券 -->
                      <template
                        v-else-if="coupon.discountType === 'percentage'"
                      >
                        {{ coupon.discountValue }} 折
                      </template>

                      <!-- ✅ 其他預設情況 -->
                      <template v-else>
                        {{ coupon.description || "一般優惠券" }}
                      </template>
                    </div>

                    <button
                      v-if="coupon.status === 'available'"
                      class="btn-primary-more use-coupon-btn"
                      @click="useCoupon(coupon)"
                    >
                      立即使用
                    </button>
                    <button v-else class="btn-disabled" disabled>
                      {{ coupon.status === "used" ? "已使用" : "已過期" }}
                    </button>
                  </div>
                </div>

                <div
                  v-if="filteredCoupons.length === 0"
                  class="empty-state-coupon"
                >
                  <i class="fas fa-ticket-alt"></i>
                  <p>暫無優惠券</p>
                </div>
              </div>
            </div>

            <!-- 帳戶設定頁面 -->
            <div v-if="activeTab === 'settings'" class="settings-section">
              <div class="section-header">
                <h2>帳戶設定</h2>
              </div>

              <div class="settings-content">
                <!-- ✅ 第三方帳號綁定 -->
                <div class="settings-group">
                  <h3>
                    <i class="fas fa-link"></i>
                    第三方帳號綁定
                  </h3>
                  <p class="section-description">綁定第三方帳號後可快速登入</p>

                  <!-- LINE 綁定 -->
                  <div class="oauth-item">
                    <div class="oauth-info">
                      <div class="oauth-icon line">
                        <i class="fab fa-line"></i>
                      </div>
                      <div class="oauth-details">
                        <h4>LINE</h4>
                        <p v-if="oauthAccounts.line.connected">已綁定</p>
                        <p v-else class="not-connected">尚未綁定</p>
                      </div>
                    </div>
                    <div class="oauth-actions">
                      <button
                        :disabled="oauthAccounts.line.connected"
                        class="btn-oauth line line-btn-pos"
                        @click="connectLine"
                      >
                        <i class="fab fa-line"></i>
                        綁定
                      </button>
                    </div>
                  </div>

                  <!-- Google 綁定 -->
                  <div class="oauth-item">
                    <div class="oauth-info">
                      <div class="oauth-icon google">
                        <i class="fab fa-google"></i>
                      </div>
                      <div class="oauth-details">
                        <h4>Google</h4>
                        <p v-if="oauthAccounts.google.connected">已綁定</p>
                        <p v-else class="not-connected">尚未綁定</p>
                      </div>
                    </div>

                    <div class="oauth-actions">
                      <button
                        :disabled="oauthAccounts.google.connected"
                        class="btn-oauth google google-btn-pos"
                        @click="connectGoogle"
                      >
                        <i class="fab fa-google"></i>
                        {{
                          oauthAccounts.google.connected
                            ? "已綁定"
                            : "綁定 Google"
                        }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- ✅ 密碼設定 -->
                <div class="settings-group">
                  <h3>
                    <i class="fas fa-shield-alt"></i>
                    密碼設定
                  </h3>
                  <div class="form-group">
                    <label>舊密碼</label>
                    <input
                      type="password"
                      v-model="passwordForm.oldPassword"
                      placeholder="請輸入目前密碼"
                    />
                  </div>
                  <div class="form-group">
                    <label>新密碼</label>
                    <input
                      type="password"
                      v-model="passwordForm.newPassword"
                      placeholder="請輸入新密碼"
                    />
                  </div>
                  <div class="form-group">
                    <label>確認新密碼</label>
                    <input
                      type="password"
                      v-model="passwordForm.confirmPassword"
                      placeholder="請再次輸入新密碼"
                    />
                  </div>
                  <button class="btn-primary-change" @click="changePassword">
                    <i class="fas fa-key"></i>
                    更改密碼
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ 申請退/換貨浮窗 -->
    <div v-if="showReturnModal" class="return-modal">
      <div class="return-modal-overlay"></div>
      <div class="return-modal-content">
        <!-- 標題 -->
        <div class="return-modal-header">
          <h3>申請退/換貨</h3>
          <button class="return-close-btn" @click="closeReturnModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- 訂單資訊 -->
        <div class="return-order-info" v-if="selectedOrderForReturn">
          <p>訂單編號：{{ selectedOrderForReturn.orderNumber }}</p>
          <p>訂單金額：{{ selectedOrderForReturn.totalAmount }}</p>
        </div>

        <!-- 表單 -->
        <form class="return-form" @submit.prevent="submitReturn">
          <!-- 1. 選擇商品 -->
          <div class="form-group">
            <label>選擇要退/換的商品 *</label>
            <div class="return-items-list">
              <div
                v-for="item in selectedOrderForReturn?.items"
                :key="item.orderDetailId"
                class="return-item-checkbox"
              >
                <label class="item-label">
                  <input
                    type="checkbox"
                    :checked="selectedReturnItems[item.orderDetailId]?.selected"
                    @change="toggleReturnItem(item.orderDetailId)"
                  />
                  <div class="item-info">
                    <span class="item-name">{{ item.productName }}</span>
                    <span class="item-qty">訂單數量：{{ item.quantity }}</span>
                    <span class="item-price">小計：{{ item.subtotal }}</span>
                  </div>
                </label>
                <!-- 數量輸入框 -->
                <div
                  v-if="selectedReturnItems[item.orderDetailId]?.selected"
                  class="item-quantity-control"
                >
                  <label class="quantity-label">退/換數量：</label>
                  <div class="quantity-input-group">
                    <button
                      type="button"
                      class="qty-btn qty-minus"
                      @click="
                        updateReturnQuantity(
                          item.orderDetailId,
                          selectedReturnItems[item.orderDetailId].quantity - 1,
                        )
                      "
                      :disabled="
                        selectedReturnItems[item.orderDetailId].quantity <= 0
                      "
                    >
                      −
                    </button>
                    <input
                      type="number"
                      class="qty-input"
                      :value="selectedReturnItems[item.orderDetailId].quantity"
                      @change="
                        updateReturnQuantity(
                          item.orderDetailId,
                          parseInt($event.target.value) || 0,
                        )
                      "
                      :min="0"
                      :max="selectedReturnItems[item.orderDetailId].maxQuantity"
                    />
                    <button
                      type="button"
                      class="qty-btn qty-plus"
                      @click="
                        updateReturnQuantity(
                          item.orderDetailId,
                          selectedReturnItems[item.orderDetailId].quantity + 1,
                        )
                      "
                      :disabled="
                        selectedReturnItems[item.orderDetailId].quantity >=
                        selectedReturnItems[item.orderDetailId].maxQuantity
                      "
                    >
                      +
                    </button>
                    <span class="qty-max"
                      >/
                      {{
                        selectedReturnItems[item.orderDetailId].maxQuantity
                      }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 2. 退/換貨類型 -->
          <div class="form-group">
            <label>請選擇服務類型 *</label>
            <div class="return-type-selector">
              <label class="type-option">
                <input
                  type="radio"
                  v-model="returnFormData.type"
                  value="return"
                />
                <span class="type-label">
                  <i class="fas fa-reply"></i> 退貨
                </span>
              </label>
              <label class="type-option">
                <input
                  type="radio"
                  v-model="returnFormData.type"
                  value="exchange"
                />
                <span class="type-label">
                  <i class="fas fa-exchange-alt"></i> 換貨
                </span>
              </label>
            </div>
          </div>

          <!-- 3. 退貨原因 -->
          <div class="form-group">
            <label>退/換貨原因 *</label>
            <select v-model="returnFormData.reason" class="reason-select">
              <option value="">-- 請選擇原因 --</option>
              <option
                v-for="reason in returnReasons"
                :key="reason.value"
                :value="reason.value"
              >
                {{ reason.label }}
              </option>
            </select>
          </div>

          <!-- 4. 備註 -->
          <div class="form-group">
            <label>備註說明</label>
            <textarea
              v-model="returnFormData.remark"
              class="return-remark"
              placeholder="如有其他說明，請在此填寫"
              rows="2"
            ></textarea>
          </div>

          <!-- 按鈕 -->
          <div class="return-modal-actions">
            <button
              type="button"
              class="btn-secondary"
              @click="closeReturnModal"
            >
              取消
            </button>
            <button type="submit" class="btn-primary">提交申請</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showReturnStatusModal" class="return-status-modal">
      <div class="return-modal-overlay"></div>

      <div class="return-status-content">
        <div class="return-status-header">
          <h3>退貨申請狀態</h3>
          <button class="return-close-btn" @click="closeReturnStatusModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="return-status-body" v-if="selectedReturnInfo">
          <p><strong>訂單編號：</strong>{{ selectedReturnInfo.orderNumber }}</p>
          <p v-if="selectedReturnInfo.orderReturnNo">
            <strong>退貨申請編號：</strong
            >{{ selectedReturnInfo.orderReturnNo }}
          </p>
          <p>
            <strong>申請時間：</strong
            >{{ formatDate(selectedReturnInfo.applyDate) }}
          </p>
          <p><strong>退貨類型：</strong>{{ selectedReturnInfo.type }}</p>
          <p><strong>原因：</strong>{{ selectedReturnInfo.reasonText }}</p>

          <p v-if="selectedReturnInfo.remark">
            <strong>備註：</strong>{{ selectedReturnInfo.remark }}
          </p>

          <p v-if="selectedReturnInfo.refundAmount">
            <strong>退款金額：</strong>NT$ {{ selectedReturnInfo.refundAmount }}
          </p>

          <hr />

          <!-- 管理員審核部分 -->
          <p>
            <strong>審核狀態：</strong>{{ selectedReturnInfo.reviewStatus }}
          </p>

          <p v-if="selectedReturnInfo.reviewMessage">
            <strong>管理員回覆：</strong>{{ selectedReturnInfo.reviewMessage }}
          </p>
        </div>
      </div>
    </div>

    <!-- 手機版底部導航 -->
    <div class="mobile-nav">
      <a
        href="#"
        :class="['mobile-nav-item', { active: activeTab === 'profile' }]"
        @click.prevent="setActiveTab('profile')"
      >
        <i class="fas fa-user"></i>
        <span>個人資料</span>
      </a>
      <a
        href="#"
        :class="['mobile-nav-item', { active: activeTab === 'orders' }]"
        @click.prevent="setActiveTab('orders')"
      >
        <i class="fas fa-shopping-bag"></i>
        <span>我的訂單</span>
      </a>
      <a
        href="#"
        :class="['mobile-nav-item', { active: activeTab === 'coupons' }]"
        @click.prevent="setActiveTab('coupons')"
      >
        <i class="fas fa-ticket-alt"></i>
        <span>優惠券</span>
      </a>
      <a
        href="#"
        :class="['mobile-nav-item', { active: activeTab === 'settings' }]"
        @click.prevent="setActiveTab('settings')"
      >
        <i class="fas fa-cog"></i>
        <span>設定</span>
      </a>
    </div>
  </div>
</template>

<script src="@/assets/js/UserDashboard.js" defer></script>

<style scoped src="@/assets/css/UserDashboard.css"></style>
